/**
 * Atlas — Admin email verification landing page.
 *
 * Reached via the link in send_email_verification(). Reads ?token=... from
 * the URL and POSTs it to /auth/admin/verify-email. On success, the backend
 * dispatches the approval email to ops and the user waits for the decision.
 *
 * See SECURITY_AUDIT C-4 — the verification step proves email ownership
 * before any approval link is generated.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'The link could not be verified.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail[0]?.msg || 'Invalid request.';
  }
  return 'The link could not be verified.';
}

export default function AdminVerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState({ status: 'verifying', message: '' });
  const ran = useRef(false);

  useEffect(() => {
    // Guard against React 18 StrictMode double-effect (the link is single-use).
    if (ran.current) return;
    ran.current = true;

    if (!token) {
      setState({ status: 'error', message: 'Missing verification token.' });
      return;
    }
    authAPI.adminVerifyEmail(token)
      .then(() => setState({
        status: 'success',
        message: 'Email verified. Your admin account request has been sent to the PropAtlas team for review. You will receive an email once a decision has been made.',
      }))
      .catch((err) => setState({ status: 'error', message: extractError(err) }));
  }, [token]);

  const isSuccess = state.status === 'success';
  const isError = state.status === 'error';
  const isVerifying = state.status === 'verifying';

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--paper)', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 460, textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
          <img src="/brand/svg/propatlas-mark.svg" className="brand-mark" alt="PropAtlas mark" style={{ width: 34 }} />
          <span className="brand-name" style={{ fontSize: 28 }}>Prop<em>A</em>tlas</span>
        </div>

        {isVerifying && (
          <div className="panel" style={{ padding: 32 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid var(--hairline)', borderTopColor: 'var(--forest)', margin: '0 auto 20px', animation: 'spin 0.8s linear infinite' }} />
            <div style={{ fontSize: 15, color: 'var(--ink-3)' }}>Verifying your email…</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {isSuccess && (
          <div className="panel" style={{ padding: 32 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--forest)', color: 'var(--paper)', display: 'grid', placeItems: 'center', fontSize: 28, margin: '0 auto 20px' }}>✓</div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 700, color: 'var(--forest)', marginBottom: 12 }}>
              Email verified
            </div>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 24 }}>
              {state.message}
            </p>
            <Link to="/login" className="btn primary" style={{ textDecoration: 'none' }}>← Back to login</Link>
          </div>
        )}

        {isError && (
          <div className="panel" style={{ padding: 32 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--crimson)', color: 'var(--paper)', display: 'grid', placeItems: 'center', fontSize: 28, margin: '0 auto 20px' }}>✕</div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 700, color: 'var(--crimson)', marginBottom: 12 }}>
              Verification failed
            </div>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 8 }}>
              {state.message}
            </p>
            <p style={{ fontSize: 13, color: 'var(--ink-4)', lineHeight: 1.6, marginBottom: 24 }}>
              Verification links expire after 30 minutes and can only be used once. If yours has
              expired, please submit a new admin request.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/admin-register" className="btn primary" style={{ textDecoration: 'none' }}>Submit new request</Link>
              <Link to="/login" className="btn" style={{ textDecoration: 'none' }}>← Back to login</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
